import { nanoid } from "nanoid";
import type { AiComponentDraft } from "../../../functions/_ai/component-schema";
import { deriveTerminalMaxAmp } from "../custom-component";
import type { ComponentDefinition, Terminal, TerminalSide } from "../types";

const DEFAULT_WIDTH = 160;
const DEFAULT_HEIGHT = 100;

export function draftToDefinition(draft: AiComponentDraft): ComponentDefinition {
  const def: ComponentDefinition = {
    id: "custom",
    name: draft.name,
    category: draft.category,
    terminals: [],
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    source: "ai",
  };

  if (draft.shortName && draft.shortName !== draft.name)
    def.shortName = draft.shortName;
  if (draft.manufacturer) def.manufacturer = draft.manufacturer;
  if (draft.model) def.model = draft.model;
  if (draft.voltage != null) def.voltage = draft.voltage;
  if (draft.capacityAh != null) def.capacityAh = draft.capacityAh;
  if (draft.watts != null) def.watts = draft.watts;
  if (draft.ampDraw != null) def.ampDraw = draft.ampDraw;
  if (draft.ampOutput != null) def.ampOutput = draft.ampOutput;
  if (draft.ampSurge != null) def.ampSurge = draft.ampSurge;
  if (draft.chemistry) def.chemistry = draft.chemistry;
  if (draft.dutyHoursPerDay != null) def.dutyHoursPerDay = draft.dutyHoursPerDay;
  if (draft.alwaysOn != null) def.alwaysOn = draft.alwaysOn;

  def.terminals = layOutTerminals(draft, deriveTerminalMaxAmp(def));

  // A field the model named but left null would mark a row the inspector never renders.
  const estimated = draft.estimatedFields.filter(
    (f) => def[f as keyof ComponentDefinition] != null,
  );
  if (estimated.length) def.aiEstimatedFields = estimated;
  if (draft.assumptions.length) def.aiAssumptions = [...draft.assumptions];

  return def;
}

function layOutTerminals(draft: AiComponentDraft, maxAmp: number): Terminal[] {
  const sideOf = (t: AiComponentDraft["terminals"][number]): TerminalSide => {
    if (t.direction === "input") return "left";
    if (t.direction === "output") return "right";
    return t.polarity === "positive" ? "right" : "left";
  };

  const sides = draft.terminals.map(sideOf);
  const seen: Record<string, number> = {};
  const counts = sides.reduce<Record<string, number>>((acc, s) => {
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});

  return draft.terminals.map((t, i) => {
    const side = sides[i];
    const index = seen[side] ?? 0;
    seen[side] = index + 1;
    return {
      id: nanoid(8),
      label: t.label,
      polarity: t.polarity,
      direction: t.direction,
      maxAmp,
      side,
      offset: (index + 1) / (counts[side] + 1),
    };
  });
}

export function clearAiEstimates(
  def: ComponentDefinition,
  edited: readonly string[],
): ComponentDefinition {
  if (!edited.length) return def;
  // Manual changes invalidate the old source for that field, even when its
  // estimate marker was already cleared by a datasheet review.
  const sourcedEdits = edited.filter(field => def.specSources?.[field]);
  if (sourcedEdits.length) {
    const specSources = { ...def.specSources };
    for (const field of sourcedEdits) delete specSources[field];
    def = { ...def, specSources };
  }
  if (!def.aiEstimatedFields?.length) return def;
  const next = def.aiEstimatedFields.filter((f) => !edited.includes(f));
  if (next.length === def.aiEstimatedFields.length) return def;
  const out = { ...def };
  if (next.length) out.aiEstimatedFields = next;
  else delete out.aiEstimatedFields;
  return out;
}
